import React, { useState, useContext, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, MessageSquare } from 'lucide-react';
import { 
  EditModeProvider, 
  EditModeContext, 
  AdminToolbar, 
  EditableText 
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactPageContent() {
  const { pageContent, setPageContent } = useContext(EditModeContext);
  const [loading, setLoading] = useState(false);
  const [siteImages, setSiteImages] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/site-images`).then(r => setSiteImages(r.data || {})).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });

  // Helper to get block content by type
  const getBlockContent = (type, defaultContent = {}) => {
    const block = pageContent?.blocks?.find(b => b.type === type);
    return block?.content || defaultContent;
  };

  // Helper to update block content
  const updateBlockContent = (type, updates) => {
    if (!pageContent) return;
    const blockIndex = pageContent.blocks?.findIndex(b => b.type === type);
    if (blockIndex >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[blockIndex] = { 
        ...newBlocks[blockIndex], 
        content: { ...newBlocks[blockIndex].content, ...updates } 
      };
      setPageContent({ ...pageContent, blocks: newBlocks });
    } else {
      const newBlock = { id: `block-${Date.now()}`, type, content: updates };
      setPageContent({ ...pageContent, blocks: [...(pageContent?.blocks || []), newBlock] });
    }
  };

  const heroContent = getBlockContent('hero', {
    title: 'Get in',
    title_accent: 'Touch',
    subtitle: 'Have questions about our products or need technical assistance? Our team is ready to help.',
  });

  const contactInfo = getBlockContent('contact_info', {
    address: '123 Industrial Avenue\nAlexandria NSW 2015\nAustralia',
    phone: '1300 TECH DIST\n(1300 832 434)',
    email: 'sales@techdistribution.com.au\nsales@techdistribution.com.au',
    hours: 'Monday - Friday: 8:00 AM - 5:00 PM\nSaturday: 9:00 AM - 1:00 PM\nSunday: Closed',
  });

  const mapContent = getBlockContent('map', {
    title: 'Find Us',
    embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3311.847674139953!2d151.19470661521072!3d-33.90359798064808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b12b1d46c59d5eb%3A0x5017d681632cdc0!2sAlexandria%20NSW%202015!5e0!3m2!1sen!2sau!4v1642000000000!5m2!1sen!2sau',
  });

  const faqContent = getBlockContent('faq', {
    title: 'Frequently Asked Questions',
    items: [
      { question: "What are your shipping options?", answer: "We offer Australia-wide shipping via Australia Post and courier services. Most orders are dispatched within 24 hours on business days." },
      { question: "How do I become a trade customer?", answer: "Simply fill out our Trade Application form with your business details and ABN. Applications are typically reviewed within 1-2 business days." },
      { question: "Do you provide technical support?", answer: "Yes! Our technical team is available during business hours to assist with product selection, installation queries, and troubleshooting." },
    ],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    setForm({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <Layout>
      <div data-testid="contact-page">
        {/* Hero */}
        <section
          className="relative py-20 bg-zinc-900"
          style={(() => {
            const img = siteImages.contact_hero?.url;
            const bgUrl = img ? (img.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img) : '';
            return bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {};
          })()}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight mb-6">
                <EditableText
                  value={heroContent.title || 'Get in'}
                  onChange={(val) => updateBlockContent('hero', { ...heroContent, title: val })}
                  tag="span"
                />
                {' '}
                <EditableText
                  value={heroContent.title_accent || 'Touch'}
                  onChange={(val) => updateBlockContent('hero', { ...heroContent, title_accent: val })}
                  tag="span"
                  className="text-[#78BE20]"
                />
              </h1>
              <EditableText
                value={heroContent.subtitle}
                onChange={(val) => updateBlockContent('hero', { ...heroContent, subtitle: val })}
                tag="p"
                className="text-xl text-zinc-300"
                multiline
              />
            </div>
          </div>
        </section>

        {/* Contact Info + Form */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Information */}
              <div className="lg:col-span-1 space-y-8">
                <div>
                  <h2 className="text-xl font-black text-foreground uppercase tracking-tight mb-6">
                    Contact Information
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                        <MapPin className="h-5 w-5 text-[#78BE20]" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium">Address</h3>
                        <EditableText
                          value={contactInfo.address}
                          onChange={(val) => updateBlockContent('contact_info', { ...contactInfo, address: val })}
                          tag="p"
                          className="text-muted-foreground text-sm mt-1 whitespace-pre-line"
                          multiline
                        />
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                        <Phone className="h-5 w-5 text-[#78BE20]" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium">Phone</h3>
                        <EditableText
                          value={contactInfo.phone}
                          onChange={(val) => updateBlockContent('contact_info', { ...contactInfo, phone: val })}
                          tag="p"
                          className="text-muted-foreground text-sm mt-1 whitespace-pre-line"
                          multiline
                        />
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                        <Mail className="h-5 w-5 text-[#78BE20]" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium">Email</h3>
                        <EditableText
                          value={contactInfo.email}
                          onChange={(val) => updateBlockContent('contact_info', { ...contactInfo, email: val })}
                          tag="p"
                          className="text-muted-foreground text-sm mt-1 whitespace-pre-line"
                          multiline
                        />
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                        <Clock className="h-5 w-5 text-[#78BE20]" />
                      </div>
                      <div>
                        <h3 className="text-foreground font-medium">Business Hours</h3>
                        <EditableText
                          value={contactInfo.hours}
                          onChange={(val) => updateBlockContent('contact_info', { ...contactInfo, hours: val })}
                          tag="p"
                          className="text-muted-foreground text-sm mt-1 whitespace-pre-line"
                          multiline
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-foreground font-bold mb-4">Quick Links</h3>
                  <ul className="space-y-2">
                    <li><a href="/trade-application" className="text-[#78BE20] hover:underline text-sm">Apply for Trade Account →</a></li>
                    <li><a href="/products" className="text-[#78BE20] hover:underline text-sm">Browse Products →</a></li>
                    <li><a href="/projects" className="text-[#78BE20] hover:underline text-sm">View Projects →</a></li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-card border border-border rounded-sm p-8">
                  <div className="flex items-center mb-6">
                    <MessageSquare className="h-6 w-6 text-[#78BE20] mr-3" />
                    <h2 className="text-xl font-black text-foreground uppercase tracking-tight">
                      Send Us a Message
                    </h2>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-background border-border" required data-testid="contact-name" />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-background border-border" required data-testid="contact-email" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input id="phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-background border-border" data-testid="contact-phone" />
                      </div>
                      <div>
                        <Label htmlFor="company">Company Name</Label>
                        <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 bg-background border-border" data-testid="contact-company" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })} required>
                        <SelectTrigger className="mt-1 bg-background border-border" data-testid="contact-subject">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sales">Sales Enquiry</SelectItem>
                          <SelectItem value="support">Technical Support</SelectItem>
                          <SelectItem value="quote">Request an Estimate</SelectItem>
                          <SelectItem value="trade">Trade Account Enquiry</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea id="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-background border-border" rows={6} required placeholder="How can we help you?" data-testid="contact-message" />
                    </div>

                    <Button type="submit" disabled={loading} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12" data-testid="contact-submit">
                      <Send className="h-4 w-4 mr-2" />
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 bg-zinc-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <EditableText
              value={mapContent.title || 'Find Us'}
              onChange={(val) => updateBlockContent('map', { ...mapContent, title: val })}
              tag="h2"
              className="text-xl font-black text-white uppercase tracking-tight text-center mb-8"
            />
            <div className="aspect-[21/9] bg-zinc-800 rounded-sm overflow-hidden">
              <iframe
                src={mapContent.embed_url}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tech Distribution Location"
              />
            </div>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <EditableText
              value={faqContent.title || 'Frequently Asked Questions'}
              onChange={(val) => updateBlockContent('faq', { ...faqContent, title: val })}
              tag="h2"
              className="text-xl font-black text-foreground uppercase tracking-tight text-center mb-8"
            />
            <div className="space-y-4">
              {(faqContent.items || []).map((faq, idx) => (
                <div key={idx} className="bg-card border border-border rounded-sm p-6">
                  <EditableText
                    value={faq.question}
                    onChange={(val) => {
                      const newItems = [...faqContent.items];
                      newItems[idx] = { ...newItems[idx], question: val };
                      updateBlockContent('faq', { ...faqContent, items: newItems });
                    }}
                    tag="h3"
                    className="text-foreground font-bold mb-2"
                  />
                  <EditableText
                    value={faq.answer}
                    onChange={(val) => {
                      const newItems = [...faqContent.items];
                      newItems[idx] = { ...newItems[idx], answer: val };
                      updateBlockContent('faq', { ...faqContent, items: newItems });
                    }}
                    tag="p"
                    className="text-muted-foreground text-sm"
                    multiline
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Additional CMS Blocks */}
      <CMSBlockRenderer excludeTypes={['hero', 'map', 'contact_form']} />

      {/* Admin Toolbar */}
      <AdminToolbar />
    </Layout>
  );
}

export default function ContactPage() {
  return (
    <EditModeProvider pageSlug="contact">
      <ContactPageContent />
    </EditModeProvider>
  );
}
